import type { ContentDashboard } from './types.js'

export const CATALOG_PATHS = {
  programs: '/admin/content',
  levels: '/admin/content/levels',
  themes: '/admin/content/themes',
  scenarios: '/admin/content/scenarios',
  tutors: '/admin/content/tutors',
} as const
export type CatalogPage = keyof typeof CATALOG_PATHS
export const CATALOG_ANCESTOR_FILTERS: Record<CatalogPage, readonly string[]> = {
  programs: [], levels: ['program'], themes: ['program', 'level'], scenarios: ['program', 'level', 'theme'], tutors: [],
}

export function catalogHref(page: CatalogPage, filters: Record<string, string | undefined> = {}): string {
  const allowed = CATALOG_ANCESTOR_FILTERS[page]
  const params = new URLSearchParams(Object.entries(filters).filter((entry): entry is [string, string] => Boolean(entry[1]) && allowed.includes(entry[0])))
  return `${CATALOG_PATHS[page]}${params.size ? `?${params}` : ''}`
}

export function catalogScope(dashboard: Pick<ContentDashboard, 'levels' | 'programs'>, params: URLSearchParams) {
  const themeId = params.get('theme') ?? ''
  const themeLevel = dashboard.levels.find(level => level.themes.some(theme => theme.id === themeId))
  const levelId = params.get('level') ?? themeLevel?.programLevelId ?? ''
  const level = dashboard.levels.find(row => row.programLevelId === levelId)
  const programId = params.get('program') ?? level?.programId ?? ''
  const program = dashboard.programs.find(row => row.id === programId)
  const valid = (!programId || Boolean(program)) && (!levelId || Boolean(level)) &&
    (!themeId || Boolean(themeLevel)) && (!level || level.programId === programId) &&
    (!themeLevel || themeLevel.programLevelId === levelId)
  return { programId, levelId, themeId, program, level, theme: themeLevel?.themes.find(row => row.id === themeId), valid }
}

export function catalogBreadcrumbs(page: CatalogPage, dashboard: Pick<ContentDashboard, 'levels' | 'programs'>, params: URLSearchParams) {
  const scope = catalogScope(dashboard, params)
  if (!scope.valid || !scope.program || page === 'programs') return []
  const items = [
    { label: 'Programs', to: catalogHref('programs') },
    { label: scope.program.name.en, to: `/admin/content/programs/${encodeURIComponent(scope.programId)}` },
  ]
  if (scope.level && page !== 'levels') items.push({ label: scope.level.level, to: catalogHref('levels', { program: scope.programId, level: scope.levelId }) })
  if (scope.theme && page === 'scenarios') items.push({ label: scope.theme.title.en, to: catalogHref('themes', { program: scope.programId, level: scope.levelId, theme: scope.themeId }) })
  return items
}

export function contentPageBreadcrumbs(pathname: string, search: string, dashboard?: ContentDashboard, diagnostic?: import('./types.js').DiagnosticDetail) {
  const params = new URLSearchParams(search)
  const page = (Object.entries(CATALOG_PATHS) as [CatalogPage, string][]).find(([, path]) => path === pathname)?.[0]
  if (page) return dashboard ? catalogBreadcrumbs(page, dashboard, params) : []
  if (pathname.startsWith('/admin/content/diagnostics/')) {
    const programId = pathname.split('/').at(-1) ?? ''
    const program = dashboard?.programs.find(row => row.id === programId)
    const programName = program?.name.en ?? diagnostic?.programName.en
    const returnParams = new URLSearchParams(params.get('returnTo') ?? `program=${programId}&kind=diagnostic`)
    return [
      { label: 'Programs', to: catalogHref('programs') },
      { label: programName ?? 'Program', to: `/admin/content/programs/${encodeURIComponent(programId)}` },
      { label: 'Scenarios', to: `${CATALOG_PATHS.scenarios}${returnParams.size ? `?${returnParams}` : ''}` },
    ]
  }
  return []
}
